<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :items-per-page="20"
      class="elevation-1"
      :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
      no-data-text="No hay contratos para mostrar"
      sort-by="activationPct"
      :sort-desc="true"
    >
      <template v-slot:top>
        <v-row class="px-4 pt-4 pb-2 align-center justify-space-between">
          <v-col cols="12" md="6" class="my-0 py-0">
            <v-text-field
              v-model="search"
              label="Buscar empresa o ID"
              dense hide-details outlined clearable
              append-icon="mdi-magnify"
            />
          </v-col>
          <v-col cols="auto" class="my-0 py-0 d-flex align-center" style="gap: 12px;">
            <div class="d-flex align-center" style="gap: 8px;">
              <span style="font-size: 13px; color: #555; white-space: nowrap;">Estado:</span>
              <v-btn-toggle v-model="statusFilter" mandatory dense class="cs-basis-toggle">
                <v-btn small value="Activo" class="cs-toggle-btn">Activos</v-btn>
                <v-btn small value="Churned" class="cs-toggle-btn">Churned</v-btn>
                <v-btn small value="Pausado" class="cs-toggle-btn">Pausados</v-btn>
                <v-btn small value="Todos" class="cs-toggle-btn">Todos</v-btn>
              </v-btn-toggle>
            </div>
            <div class="d-flex align-center" style="gap: 8px;">
              <span style="font-size: 13px; color: #555; white-space: nowrap;">Membresías:</span>
              <v-btn-toggle v-model="basis" mandatory dense class="cs-basis-toggle">
                <v-btn small value="active" class="cs-toggle-btn">Activas</v-btn>
                <v-btn small value="historical" class="cs-toggle-btn">Totales</v-btn>
              </v-btn-toggle>
            </div>
            <v-btn
              small height="39"
              class="excel-button"
              :disabled="loading || items.length === 0"
              @click="downloadExcel"
            >
              <v-icon small left>mdi-microsoft-excel</v-icon>
              Excel
            </v-btn>
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.producto="{ item }">
        {{ item.producto || '—' }}
      </template>

      <template v-slot:item.membershipQuota="{ item }">
        <span v-if="item.membershipQuota !== null">{{ item.membershipQuota }}</span>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.endDate="{ item }">
        <span v-if="item.endDate">{{ item.endDate | date }}</span>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip x-small :color="statusColor(item.status)" dark>{{ item.status || '—' }}</v-chip>
      </template>

      <template v-slot:item.activationPct="{ item }">
        <v-chip
          v-if="item.activationPct !== null"
          small
          :color="scoreColor(item.activationPct)"
          class="white--text font-weight-bold"
          :title="activationTooltip(item)"
        >
          {{ item.activationPct }}%
        </v-chip>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.usagePct="{ item }">
        <v-chip v-if="item.activeMemberships > 0" small :color="scoreColor(item.usagePct)" class="white--text font-weight-bold">
          {{ item.usagePct }}%
        </v-chip>
        <span v-else class="grey--text">—</span>
      </template>
    </v-data-table>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="3000" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import CompanyUsageService from '@/services/company-usage.service';
import DateUtils from '@/utils/date.utils';
import { utilsMixin } from '@/mixins/utilsMixin';

export default {
  name: 'ContractSuccessTable',
  mixins: [utilsMixin],

  data() {
    return {
      loading: false,
      rows: [],
      search: '',
      basis: 'active',
      statusFilter: 'Activo',
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'green',
    };
  },

  computed: {
    headers() {
      return [
        { text: 'ID',                    value: 'codigo',               sortable: true },
        { text: 'Empresa',               value: 'company',              sortable: true },
        { text: 'Producto',              value: 'producto',             sortable: true },
        { text: 'Fecha de vencimiento',  value: 'endDate',              sortable: true },
        { text: 'Estado',                value: 'status',               sortable: true },
        { text: 'Usuarios cargados',     value: 'totalUsers',           sortable: true },
        { text: 'Membresías contratadas', value: 'membershipQuota',     sortable: true },
        { text: this.basis === 'active' ? 'Membresías activas' : 'Membresías totales', value: 'activeMemberships', sortable: true },
        { text: '% Activación',          value: 'activationPct',        sortable: true },
        { text: 'Membresías con Uso',    value: 'membershipsWithActivity', sortable: true },
        { text: '% Uso',                 value: 'usagePct',             sortable: true },
      ];
    },

    items() {
      const term = (this.search || '').trim().toLowerCase();
      if (!term) return this.rows;
      return this.rows.filter(
        (r) =>
          (r.company || '').toLowerCase().includes(term) ||
          (r.codigo || '').toLowerCase().includes(term)
      );
    },
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    scoreColor(pct) {
      if (pct >= 70) return 'green';
      if (pct >= 40) return 'orange';
      return 'red';
    },
    // La base del % cambia según la línea de negocio: en Employee Benefit se
    // mide contra la cuota vendida (superar 100% es esperable, se cobra el
    // excedente) y en el resto contra los usuarios cargados.
    activationTooltip(item) {
      return item.activationBasis === 'quota'
        ? `${item.activeMemberships} membresías activas sobre ${item.membershipQuota} contratadas`
        : `${item.activatedUsers} usuarios activados sobre ${item.totalUsers} cargados`;
    },
    // Mismos colores que la tabla de Contratos, para consistencia visual.
    statusColor(s) {
      if (s === 'Activo') return 'green darken-1';
      if (s === 'Pausado') return 'orange darken-1';
      return 'grey darken-1';
    },
    async fetchData() {
      this.loading = true;
      const res = await CompanyUsageService.getContractSuccess({
        basis: this.basis,
        status: this.statusFilter,
      });
      if (res.status === 'ok') {
        this.rows = res.data.data;
      } else {
        this.rows = [];
        this.showErrorSnackbar('Ocurrió un error al cargar los contratos');
      }
      this.loading = false;
    },
    downloadExcel() {
      if (this.items.length === 0) return;
      const sheetData = this.items.map((r) => ({
        ID: r.codigo,
        Empresa: r.company,
        Producto: r.producto || '',
        'Fecha de vencimiento': r.endDate ? DateUtils.format(r.endDate) : '',
        Estado: r.status || '',
        'Usuarios cargados': r.totalUsers,
        'Membresías contratadas': r.membershipQuota !== null ? r.membershipQuota : 'N/A',
        'Membresías activas': r.activeMemberships,
        '% Activación': r.activationPct !== null ? r.activationPct : 'N/A',
        'Base de activación': r.activationBasis === 'quota'
          ? 'Cuota contratada'
          : 'Usuarios cargados',
        'Membresías con Uso': r.membershipsWithActivity,
        '% Uso': r.activeMemberships > 0 ? r.usagePct : 'N/A',
      }));
      const ws = XLSX.utils.json_to_sheet(sheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Customer Success');
      XLSX.writeFile(wb, 'customer-success.xlsx');
    },
  },

  watch: {
    basis() {
      this.fetchData();
    },
    statusFilter() {
      this.fetchData();
    },
  },
};
</script>

<style scoped>
.excel-button {
  background-color: #4caf50 !important;
  color: white;
}
.excel-button:hover {
  background-color: #78d67c !important;
}
.cs-basis-toggle {
  border-radius: 8px !important;
  overflow: hidden;
  background-color: #fff !important;
}
.cs-toggle-btn {
  font-size: 12px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  padding: 0 14px !important;
}
.cs-basis-toggle .v-btn--active {
  background-color: #104B94 !important;
  color: #fff !important;
}
</style>
